import {
  Box,
  Button,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  Portal,
  Text,
} from '@chakra-ui/react'
import {
  ContentNodeUtils,
  DataTypeConfigUtils,
  InputObjectDataTypeConfig,
  InputValueContentNode,
  InputValueDataTypeConfig,
} from '@helo/cms'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  getBoundaryProps,
  useClickOutsideBoundary,
} from '../../../hooks/useClickOutsideBoundary'
import { useIsDark } from '../../../hooks/useIsDark'
import { Icon } from '../../Icons/Icon'
import {
  LoadOptionsForPickListFn,
  getFilterObjectContentNode,
  hasFilterValue,
} from '../FilterEditor/FilterEditor.parts'
import { FilterEditorRow } from '../FilterEditorRow'

export interface ColumnFilterEditorProps {
  /** The field/column key this filter targets. */
  fieldKey: string
  label?: string
  /** Shared root filter input value content node (same node the main FilterEditor edits). */
  filterInputValue: InputValueContentNode
  /** Called on Apply / Clear with the updated root filter content node. */
  onChange: (updatedRoot: InputValueContentNode) => void
  /**
   * Available filter field configs. When omitted, derived from `filterInputValue`'s
   * input object config so the component can be used standalone (e.g. Storybook).
   */
  availableFilters?: InputValueDataTypeConfig[]
  /** Async load options for the "Is Any Of" pick list (distinct values for this field). */
  loadOptionsForPickList?: LoadOptionsForPickListFn
  /** Overrides Chakra color mode for dark/light styling (e.g. from widget config). */
  colorMode?: 'light' | 'dark'
  /** When true, the filter card is open on first render (e.g. for Storybook). */
  defaultIsPopoverOpen?: boolean
  /** Controlled open state for hosts that coordinate multiple column filters. */
  isPopoverOpen?: boolean
  /** Called whenever the popover requests an open-state change. */
  onPopoverOpenChange?: (isOpen: boolean) => void
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Focused, single-column filter editor used from a data-table column header.
 *
 * Renders a compact filter icon button; activating it opens a card with the
 * operator (condition) dropdown and value input only — stacked vertically and
 * scoped to the selected column. The footer exposes Clear, Cancel, and Apply.
 *
 * It edits the same shared filter content node the main FilterEditor uses, so
 * applied column-header filters surface as standard chips and rows in the main
 * filter modal. On Apply the field is appended to the end of the filter object
 * so it shows at the bottom of the modal's applied list.
 */
export const ColumnFilterEditor = ({
  fieldKey,
  label,
  filterInputValue,
  onChange,
  availableFilters,
  loadOptionsForPickList,
  colorMode: colorModeOverride,
  defaultIsPopoverOpen = false,
  isPopoverOpen,
  onPopoverOpenChange,
  ...props
}: ColumnFilterEditorProps) => {
  const isDark = useIsDark(colorModeOverride)
  const [uncontrolledIsOpen, setUncontrolledIsOpen] =
    useState(!!defaultIsPopoverOpen)
  const isOpen = isPopoverOpen ?? uncontrolledIsOpen
  const popoverContentRef = useRef<HTMLDivElement>(null)

  const setPopoverOpen = useCallback(
    (nextIsOpen: boolean | ((currentIsOpen: boolean) => boolean)) => {
      const nextOpen =
        typeof nextIsOpen === 'function' ? nextIsOpen(isOpen) : nextIsOpen

      if (isPopoverOpen === undefined) {
        setUncontrolledIsOpen(nextOpen)
      }
      onPopoverOpenChange?.(nextOpen)
    },
    [isOpen, isPopoverOpen, onPopoverOpenChange],
  )

  // Boundary that marks both the trigger button and the card as "inside", so
  // clicking the trigger to toggle the card closed isn't treated as an outside
  // click (which would immediately re-open it).
  const boundaryId = `column-filter-${fieldKey}`

  const filters = useMemo(() => {
    if (availableFilters) return availableFilters
    try {
      const objectConfig = getFilterObjectContentNode(
        filterInputValue,
      ).getDataTypeConfig() as InputObjectDataTypeConfig
      return DataTypeConfigUtils.getFields()(
        objectConfig,
      ) as InputValueDataTypeConfig[]
    } catch {
      return []
    }
  }, [availableFilters, filterInputValue])

  const fieldConfig = useMemo(
    () => filters.find(config => config.key === fieldKey),
    [filters, fieldKey],
  )

  const appliedFieldValue = useMemo(() => {
    try {
      const data = getFilterObjectContentNode(filterInputValue).data
      if (!isRecord(data)) return undefined
      return data[fieldKey]
    } catch {
      return undefined
    }
  }, [filterInputValue, fieldKey])

  const hasFilter = hasFilterValue(appliedFieldValue)

  /** Working content node for this field, seeded from the applied value. */
  const baseFieldContentNode = useMemo(() => {
    if (!fieldConfig) return undefined
    const value = isRecord(appliedFieldValue) ? appliedFieldValue : {}
    return ContentNodeUtils.createContentNodeWithSelection(
      fieldConfig,
      value,
    ) as InputValueContentNode
  }, [fieldConfig, appliedFieldValue])

  const [latestFieldContentNode, setLatestFieldContentNode] = useState<
    InputValueContentNode | undefined
  >(baseFieldContentNode)
  const wasOpenRef = useRef(isOpen)

  // Seed from the applied value on open, then preserve in-progress edits while
  // unrelated table/query state refreshes recreate the applied content node.
  useEffect(() => {
    const wasOpen = wasOpenRef.current

    if (isOpen) {
      setLatestFieldContentNode(current =>
        !wasOpen || !current ? baseFieldContentNode : current,
      )
    } else {
      setLatestFieldContentNode(baseFieldContentNode)
    }

    wasOpenRef.current = isOpen
  }, [isOpen, baseFieldContentNode])

  // Close on click outside. The shared hook treats clicks inside the card
  // (popoverContentRef), inside the boundary-marked trigger, and inside floating
  // UI (react-select menus, date calendars, Chakra portals) as "inside".
  useClickOutsideBoundary({
    ref: popoverContentRef,
    boundaryId,
    onClickOutside: () => setPopoverOpen(false),
    enabled: isOpen,
  })

  // Place the text cursor in the value input by default when the card opens.
  // The default "Is Any Of" pick list owns its own search input that isn't a
  // native autoFocus target, so focus is applied after layout settles. A test
  // (FocusesValueInputByDefault) guards these testids against silent breakage.
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    const focusValue = () => {
      if (cancelled) return
      const content = popoverContentRef.current
      if (!content) return
      const valueInput = content.querySelector<HTMLElement>(
        '[data-testid="filter-picklist-search-input"], [data-testid="string-filter-value-input"], [data-testid="number-filter-value-input"], [data-testid="string-filter-inlist-input"]',
      )
      valueInput?.focus()
    }
    const raf = requestAnimationFrame(() => requestAnimationFrame(focusValue))
    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
    }
  }, [isOpen])

  const canEdit = !!fieldConfig && !!latestFieldContentNode

  const hasApplyChanges = useMemo(() => {
    if (!latestFieldContentNode) return false
    return (
      JSON.stringify(latestFieldContentNode.data ?? {}) !==
      JSON.stringify(isRecord(appliedFieldValue) ? appliedFieldValue : {})
    )
  }, [latestFieldContentNode, appliedFieldValue])

  /**
   * Write the field value into the shared filter object.
   *
   * A brand-new field is appended last (so it shows at the bottom of the main
   * modal's applied list). Re-editing an already-applied field reassigns its key
   * in place, preserving its existing position rather than moving it to the end.
   */
  const commitFieldValue = (value: Record<string, unknown> | undefined) => {
    const filterObject = getFilterObjectContentNode(filterInputValue)
    const existing = isRecord(filterObject.data) ? { ...filterObject.data } : {}
    let merged: Record<string, unknown>
    if (value && hasFilterValue(value)) {
      // Reassigning an existing key keeps its insertion position; a new key is
      // appended at the end.
      merged = { ...existing, [fieldKey]: value }
    } else {
      merged = { ...existing }
      delete merged[fieldKey]
    }
    const updatedFilterObject =
      ContentNodeUtils.updateContentNode(merged)(filterObject)
    const updatedRoot = ContentNodeUtils.updateChildContentNode(
      updatedFilterObject,
    )(filterInputValue) as InputValueContentNode
    onChange(updatedRoot)
  }

  const handleApply = () => {
    if (!canEdit || !latestFieldContentNode) {
      setPopoverOpen(false)
      return
    }
    commitFieldValue(latestFieldContentNode.data as Record<string, unknown>)
    setPopoverOpen(false)
  }

  const handleClear = () => {
    commitFieldValue(undefined)
    setPopoverOpen(false)
  }

  const handleCancel = () => setPopoverOpen(false)

  const triggerLabel = label
    ? `Filter ${label}`
    : hasFilter
      ? 'Edit filter'
      : 'Filter column'

  return (
    <Popover
      isOpen={isOpen}
      onOpen={() => setPopoverOpen(true)}
      onClose={() => setPopoverOpen(false)}
      placement="bottom-end"
      closeOnBlur={false}
      isLazy
      lazyBehavior="unmount"
    >
      <PopoverTrigger>
        <Box display="inline-flex">
          <Box
            as="button"
            type="button"
            className="column-filter-trigger"
            data-testid={`column-filter-button-${fieldKey}`}
            data-has-filter={hasFilter ? 'true' : 'false'}
            aria-label={triggerLabel}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            onClick={() => setPopoverOpen(open => !open)}
            {...getBoundaryProps(boundaryId)}
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            boxSize="20px"
            flexShrink={0}
            borderRadius="2px"
            cursor="pointer"
            bg={isOpen ? 'presets.neutral.30' : 'transparent'}
            _hover={{
              bg: hasFilter ? 'presets.neutral.30' : 'presets.neutral.20',
            }}
            _active={{ bg: 'presets.neutral.40' }}
            {...props}
          >
            <Icon
              icon="FilterIcon"
              boxSize="12px"
              color="presets.turquoise.100"
            />
          </Box>
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          ref={popoverContentRef}
          data-popover-card
          data-testid={`column-filter-card-${fieldKey}`}
          borderRadius="4px"
          boxShadow={
            isDark
              ? '0 4px 24px 0 rgba(0, 0, 0, 0.24)'
              : '0px 6px 20px 0px rgba(0, 0, 0, 0.08), 0px 8px 30px 0px rgba(0, 0, 0, 0.10)'
          }
          minWidth="320px"
          width="320px"
          bg={isDark ? 'presets.dark.surfacePanel' : undefined}
          border={isDark ? '1px solid' : undefined}
          borderColor={isDark ? 'presets.neutral.90' : undefined}
          onKeyDownCapture={e => {
            if (e.key !== 'Enter') return
            const target = e.target as HTMLElement
            if (
              target.closest('button, a[href], [role="button"], [role="link"]')
            )
              return
            const controlWithOpenMenu = target.closest(
              '[data-part="Control"][data-menu-open="true"]',
            )
            // The pick list menu portals to <body>, so it can't be found via the
            // card subtree. Guard against matching another surface's menu by also
            // requiring focus to be inside this card's pick list.
            const isInPickListWithOpenMenu =
              !!target.closest('[data-testid="filter-pick-list-filter"]') &&
              !!popoverContentRef.current?.contains(document.activeElement) &&
              !!document.querySelector('[data-testid="filter-pick-list-menu"]')
            if (controlWithOpenMenu || isInPickListWithOpenMenu) return
            e.preventDefault()
            handleApply()
          }}
        >
          <PopoverBody
            padding="16px"
            bg={isDark ? 'presets.dark.surfacePanel' : undefined}
            color={isDark ? 'presets.neutral.0' : undefined}
          >
            {canEdit && latestFieldContentNode ? (
              <FilterEditorRow
                layout="stacked"
                hideFieldSelect
                inputValueContentNode={latestFieldContentNode}
                filters={filters}
                onUpdate={setLatestFieldContentNode}
                showRemoveButton={false}
                autoFocus
                loadOptionsForPickList={loadOptionsForPickList}
                isFilterEditorOpen={isOpen}
                colorMode={colorModeOverride}
              />
            ) : (
              <Text fontSize="12px" color="presets.neutral.60">
                This column can&apos;t be filtered.
              </Text>
            )}
          </PopoverBody>
          <PopoverFooter
            padding="16px"
            bg={isDark ? 'presets.dark.surfacePanel' : undefined}
            color={isDark ? 'presets.neutral.0' : undefined}
            borderTop={isDark ? '1px solid' : undefined}
            borderColor={isDark ? 'presets.neutral.90' : undefined}
          >
            <HStack gap="8px" justifyContent="space-between">
              <Button
                data-testid="column-filter-clear-button"
                size="sm"
                variant="tertiary"
                onClick={handleClear}
              >
                Clear
              </Button>
              <HStack gap="8px" flexShrink={0}>
                <Button
                  data-testid="column-filter-cancel-button"
                  size="sm"
                  variant="tertiary"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="column-filter-apply-button"
                  size="sm"
                  variant="primary"
                  onClick={handleApply}
                  isDisabled={!canEdit || !hasApplyChanges}
                >
                  Apply
                </Button>
              </HStack>
            </HStack>
          </PopoverFooter>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}

ColumnFilterEditor.displayName = 'ColumnFilterEditor'
